import { useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';

interface GlobalAdminPermission {
    type: 'globalAdmin';
}

interface PropertyAdminPermission {
    type: 'propertyAdmin';
    propertyId: number;
}

export type Permission = GlobalAdminPermission | PropertyAdminPermission;

interface CurrentUser {
    id: number;
    username: string;
    name: string;
    permissions: Permission[];
}

interface UseCurrentUserResult {
    user: CurrentUser | undefined;
    loading: boolean;
    error: Error | undefined;
    isGlobalAdmin: () => boolean;
}

const useCurrentUser = (): UseCurrentUserResult => {
    const [user, setUser] = useState<CurrentUser | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | undefined>(undefined);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            // Check if there's a token before making the API call
            // This prevents infinite loops when on the login page
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                setUser(undefined);
                return;
            }

            try {
                setLoading(true);
                const response = await apiRawFetch('api/auth/verify');

                if (!response.ok) {
                    throw new Error('Failed to verify authentication');
                }

                const data = await response.json();

                if (data.valid && data.user) {
                    setUser(data.user);
                    if (data.token) {
                        localStorage.setItem('token', data.token);
                    }
                } else {
                    setUser(undefined);
                }
            } catch (err) {
                setError(
                    err instanceof Error ? err : new Error('Unknown error')
                );
                setUser(undefined);
            } finally {
                setLoading(false);
            }
        };

        fetchCurrentUser();
    }, []);

    const isGlobalAdmin = (): boolean => {
        return user?.permissions?.some(p => p.type === 'globalAdmin') ?? false;
    };

    return { user, loading, error, isGlobalAdmin };
};

export default useCurrentUser;
